/*
 * @bot-written
 *
 * WARNING AND NOTICE
 * Any access, download, storage, and/or use of this source code is subject to the terms and conditions of the
 * Full Software Licence as accepted by you before being granted access to this source code and other materials,
 * the terms of which can be accessed on the Codebots website at https://codebots.com/full-software-licence. Any
 * commercial use in contravention of the terms of the Full Software Licence may be pursued by Codebots through
 * licence termination and further legal action, and be required to indemnify Codebots for any loss or damage,
 * including interest and costs. You are deemed to have accepted the terms of the Full Software Licence on any
 * access, download, storage, and/or use of this source code.
 *
 * BOT WARNING
 * This file is bot-written.
 * Any changes out side of "protected regions" will be lost next time the bot makes any changes.
 */
import { action, observable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from "Util/AttributeUtils";
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import { VisitorsAddressEntity } from 'Models/Security/Acl/VisitorsAddressEntity';
import { AptusAdminAddressEntity } from 'Models/Security/Acl/AptusAdminAddressEntity';
import { UserAddressEntity } from 'Models/Security/Acl/UserAddressEntity';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import {SuperAdministratorScheme} from '../Security/Acl/SuperAdministratorScheme';
// % protected region % [Add any further imports here] off begin
// % protected region % [Add any further imports here] end

export interface IAddressEntityAttributes extends IModelAttributes {
	name: string;
	addressLine1: string;
	addressLine2: string;
	suburb: string;
	state: string;
	postcode: string;
	country: string;

	defaultDeliveryAddresss: Array<Models.ProjectEntity | Models.IProjectEntityAttributes>;
	orderDeliveryAddresss: Array<Models.OrderEntity | Models.IOrderEntityAttributes>;
	organisationId?: string;
	organisation?: Models.OrganisationEntity | Models.IOrganisationEntityAttributes;
	// % protected region % [Add any custom attributes to the interface here] off begin
	// % protected region % [Add any custom attributes to the interface here] end
}

// % protected region % [Customise your entity metadata here] off begin
@entity('AddressEntity', 'Address')
// % protected region % [Customise your entity metadata here] end
export default class AddressEntity extends Model implements IAddressEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsAddressEntity(),
		new AptusAdminAddressEntity(),
		new UserAddressEntity(),
		// % protected region % [Add any further ACL entries here] off begin
		// % protected region % [Add any further ACL entries here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [
		// % protected region % [Add any custom create exclusions here] off begin
		// % protected region % [Add any custom create exclusions here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [
		// % protected region % [Add any custom update exclusions here] off begin
		// % protected region % [Add any custom update exclusions here] end
	];

	// % protected region % [Modify props to the crud options here for attribute 'Name'] off begin
	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		name: 'Name',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public name: string;
	// % protected region % [Modify props to the crud options here for attribute 'Name'] end

	// % protected region % [Modify props to the crud options here for attribute 'Address Line 1'] off begin
	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		name: 'Address Line 1',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public addressLine1: string;
	// % protected region % [Modify props to the crud options here for attribute 'Address Line 1'] end

	// % protected region % [Modify props to the crud options here for attribute 'Address Line 2'] off begin
	@observable
	@attribute()
	@CRUD({
		name: 'Address Line 2',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public addressLine2: string;
	// % protected region % [Modify props to the crud options here for attribute 'Address Line 2'] end

	// % protected region % [Modify props to the crud options here for attribute 'Suburb'] off begin
	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		name: 'Suburb',
		displayType: 'textfield',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public suburb: string;
	// % protected region % [Modify props to the crud options here for attribute 'Suburb'] end

	// % protected region % [Modify props to the crud options here for attribute 'State'] off begin
	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		name: 'State',
		displayType: 'textfield',
		order: 50,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public state: string;
	// % protected region % [Modify props to the crud options here for attribute 'State'] end

	// % protected region % [Modify props to the crud options here for attribute 'Postcode'] off begin
	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		name: 'Postcode',
		displayType: 'textfield',
		order: 60,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public postcode: string;
	// % protected region % [Modify props to the crud options here for attribute 'Postcode'] end

	// % protected region % [Modify props to the crud options here for attribute 'Country'] off begin
	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		name: 'Country',
		displayType: 'textfield',
		order: 70,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public country: string;
	// % protected region % [Modify props to the crud options here for attribute 'Country'] end

	/**
	 * Project has a default delivery address set
	 */
	@observable
	@attribute({isReference: true})
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Default Delivery Address'] off begin
		name: "Default Delivery Addresss",
		displayType: 'reference-multicombobox',
		order: 80,
		referenceTypeFunc: () => Models.ProjectEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'defaultDeliveryAddresss',
			oppositeEntity: () => Models.ProjectEntity,
		}),
		// % protected region % [Modify props to the crud options here for reference 'Default Delivery Address'] end
	})
	public defaultDeliveryAddresss: Models.ProjectEntity[] = [];

	/**
	 * Order must have a delivery address
	 */
	@observable
	@attribute({isReference: true})
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Order Delivery Address'] off begin
		name: "Order Delivery Addresss",
		displayType: 'reference-multicombobox',
		order: 90,
		referenceTypeFunc: () => Models.OrderEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'orderDeliveryAddresss',
			oppositeEntity: () => Models.OrderEntity,
		}),
		// % protected region % [Modify props to the crud options here for reference 'Order Delivery Address'] end
	})
	public orderDeliveryAddresss: Models.OrderEntity[] = [];

	/**
	 * Organisation can have multiple addresses (Head office, Yard address)
	 */
	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Organisation'] off begin
		name: 'Organisation',
		displayType: 'reference-combobox',
		order: 100,
		referenceTypeFunc: () => Models.OrganisationEntity,
		// % protected region % [Modify props to the crud options here for reference 'Organisation'] end
	})
	public organisationId?: string;
	@observable
	@attribute({isReference: true})
	public organisation: Models.OrganisationEntity;

	// % protected region % [Add any custom attributes to the model here] off begin
	// % protected region % [Add any custom attributes to the model here] end

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IAddressEntityAttributes>) {
		// % protected region % [Add any extra constructor logic before calling super here] off begin
		// % protected region % [Add any extra constructor logic before calling super here] end

		super(attributes);

		// % protected region % [Add any extra constructor logic after calling super here] off begin
		// % protected region % [Add any extra constructor logic after calling super here] end
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IAddressEntityAttributes>) {
		// % protected region % [Override assign attributes here] off begin
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.addressLine1 !== undefined) {
				this.addressLine1 = attributes.addressLine1;
			}
			if (attributes.addressLine2 !== undefined) {
				this.addressLine2 = attributes.addressLine2;
			}
			if (attributes.suburb !== undefined) {
				this.suburb = attributes.suburb;
			}
			if (attributes.state !== undefined) {
				this.state = attributes.state;
			}
			if (attributes.postcode !== undefined) {
				this.postcode = attributes.postcode;
			}
			if (attributes.country !== undefined) {
				this.country = attributes.country;
			}
			if (attributes.defaultDeliveryAddresss !== undefined && Array.isArray(attributes.defaultDeliveryAddresss)) {
				for (const model of attributes.defaultDeliveryAddresss) {
					if (model instanceof Models.ProjectEntity) {
						this.defaultDeliveryAddresss.push(model);
					} else {
						this.defaultDeliveryAddresss.push(new Models.ProjectEntity(model));
					}
				}
			}
			if (attributes.orderDeliveryAddresss !== undefined && Array.isArray(attributes.orderDeliveryAddresss)) {
				for (const model of attributes.orderDeliveryAddresss) {
					if (model instanceof Models.OrderEntity) {
						this.orderDeliveryAddresss.push(model);
					} else {
						this.orderDeliveryAddresss.push(new Models.OrderEntity(model));
					}
				}
			}
			if (attributes.organisationId !== undefined) {
				this.organisationId = attributes.organisationId;
			}
			if (attributes.organisation !== undefined) {
				if (attributes.organisation === null) {
					this.organisation = attributes.organisation;
				} else {
					if (attributes.organisation instanceof Models.OrganisationEntity) {
						this.organisation = attributes.organisation;
						this.organisationId = attributes.organisation.id;
					} else {
						this.organisation = new Models.OrganisationEntity(attributes.organisation);
						this.organisationId = this.organisation.id;
					}
				}
			}
			// % protected region % [Override assign attributes here] end

			// % protected region % [Add any extra assign attributes logic here] off begin
			// % protected region % [Add any extra assign attributes logic here] end
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	// % protected region % [Customize Default Expands here] off begin
	public defaultExpands = `
		defaultDeliveryAddresss {
			${Models.ProjectEntity.getAttributes().join('\n')}
		}
		orderDeliveryAddresss {
			${Models.OrderEntity.getAttributes().join('\n')}
		}
		organisation {
			${Models.OrganisationEntity.getAttributes().join('\n')}
		}
	`;
	// % protected region % [Customize Default Expands here] end

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// % protected region % [Customize Save From Crud here] off begin
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath = {
			defaultDeliveryAddresss: {},
			orderDeliveryAddresss: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'defaultDeliveryAddresss',
							'orderDeliveryAddresss',
						]
					},
				],
			}
		);
	}
	// % protected region % [Customize Save From Crud here] end

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		// % protected region % [Customise the display name for this entity] on begin
		return this.name;
		// % protected region % [Customise the display name for this entity] end
	}


	// % protected region % [Add any further custom model features here] off begin
	// % protected region % [Add any further custom model features here] end
}

// % protected region % [Modify the create and modified CRUD attributes here] off begin
/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [ createdAttr, modifiedAttr ] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(AddressEntity.prototype, 'created');
CRUD(modifiedAttr)(AddressEntity.prototype, 'modified');
// % protected region % [Modify the create and modified CRUD attributes here] end
